import { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../hooks/useAuth'
import { scanTranscript, useSermonAdmin, generateSermonSummary } from '../hooks/useSermons'
import { canScan, recordScans } from '../lib/scanLimits'
import { resolveVideoDate, extractApiDateFromMeta } from './AdminPanel'

// ── Premium black/gold onboarding for a church's first-run setup ──────────
// Two steps: (1) church basics, (2) the "wow moment" — paste a sermon URL,
// watch the scan animate, then reveal every Bible reference found. On a
// successful scan we create the church, save the sermon, flip the user to
// admin and mark setup complete. Mirrors the app's existing inline-style
// black/gold visual language (see Onboarding.jsx / Auth.jsx).
// MAX_URLS is intentionally 1 today; the input/scan flow is written to handle
// a small batch should that change.

const ACCENT = 'var(--gold)'
const NAVY   = 'var(--ink)'
const SERIF  = "'Source Serif 4', Georgia, serif"
const SANS   = "'Plus Jakarta Sans', system-ui, sans-serif"
const MAX_URLS = 1

// YouTube URL → 11-char video id (mirrors AdminPanel/SermonUpload helpers).
function extractVideoId(raw) {
  const s = String(raw || '').trim()
  if (!s) return null
  const m = s.match(/(?:v=|youtu\.be\/|embed\/|shorts\/|\/live\/)([A-Za-z0-9_-]{11})/)
  if (m) return m[1]
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return s
  return null
}

// lowercase, non-alphanum → '-', strip leading/trailing dashes. Matches the
// slug helper in useAuth.createChurch so slugs stay consistent.
function nameToSlug(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

// Flatten a ScrapeCreators segment array into plain text (fallback when the
// edge function doesn't return transcript_only_text).
function flattenSegs(segs) {
  if (!Array.isArray(segs)) return ''
  return segs.map(s => String(s?.text ?? '').trim()).filter(Boolean).join(' ')
}

// Seconds → "M:SS" for the reference timestamps. Returns '' when absent.
function fmtTimestamp(r) {
  if (r?.timestamp) return String(r.timestamp)
  const sec = r?.timestamp_s ?? r?.timestampS
  if (sec == null || Number.isNaN(Number(sec))) return ''
  const n = Math.max(0, Math.round(Number(sec)))
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`
}

const SCAN_STEPS = [
  'Fetching transcript…',
  'Reading through the sermon…',
  'Finding Bible references…',
  'Linking to your Bible…',
]

const s = {
  shell:   { minHeight:'100vh', background:'var(--cream)', color:'var(--ink)', fontFamily:SANS, display:'flex', flexDirection:'column', paddingTop:'env(safe-area-inset-top)', paddingBottom:'env(safe-area-inset-bottom)' },
  body:    { flex:1, padding:'40px 24px 28px', maxWidth:460, width:'100%', margin:'0 auto', boxSizing:'border-box' },
  kicker:  { display:'flex', alignItems:'center', gap:10, marginBottom:22 },
  kline:   { width:26, height:2, background:ACCENT, borderRadius:2 },
  klabel:  { fontSize:11, fontWeight:800, letterSpacing:'0.18em', textTransform:'uppercase', color:'var(--gold-ink)', fontFamily:SANS },
  title:   { fontFamily:SERIF, fontSize:34, lineHeight:1.12, color:'var(--ink)', marginBottom:10 },
  subtitle:{ fontSize:14.5, color:'var(--stone)', marginBottom:26, lineHeight:1.55 },
  label:   { fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--stone)', marginBottom:8, display:'block' },
  input:   { width:'100%', background:'var(--white)', border:'1px solid var(--border-strong)', borderRadius:12, padding:'15px 16px', color:'var(--ink)', fontSize:16, fontFamily:SANS, boxSizing:'border-box', outline:'none' },
  inputLg: { width:'100%', background:'var(--white)', border:'1px solid var(--border-strong)', borderRadius:12, padding:'17px 16px', color:'var(--ink)', fontSize:18, fontFamily:SANS, boxSizing:'border-box', outline:'none' },
  primary: { width:'100%', background:ACCENT, color:NAVY, border:'none', borderRadius:12, padding:'16px', fontSize:15.5, fontWeight:800, cursor:'pointer', fontFamily:SANS },
  outline: { width:'100%', background:'transparent', color:'var(--ink)', border:'1px solid var(--border-strong)', borderRadius:12, padding:'15px', fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:SANS },
  ghost:   { display:'block', margin:'18px auto 0', background:'none', border:'none', color:'var(--stone)', fontSize:13.5, fontWeight:600, cursor:'pointer', fontFamily:SANS },
  dis:     { opacity:0.4, cursor:'default' },
  err:     { background:'rgba(249,112,102,0.12)', border:'1px solid rgba(249,112,102,0.3)', color:'#C8372A', borderRadius:10, padding:'11px 13px', fontSize:13, marginTop:14, lineHeight:1.5 },
}

// Small YouTube glyph for the "Watch the moment" links.
function YouTubeIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
      <rect x="1" y="4" width="22" height="16" rx="4.5" fill="#FF0000"/>
      <path d="M10 8.5l6 3.5-6 3.5z" fill="#fff"/>
    </svg>
  )
}

// YouTube deep link to a specific second of a video.
function watchAtUrl(videoId, r) {
  if (!videoId) return null
  const sec = r?.timestamp_s ?? r?.timestampS
  const t = sec != null && !Number.isNaN(Number(sec)) ? `&t=${Math.max(0, Math.floor(Number(sec)))}s` : ''
  return `https://www.youtube.com/watch?v=${videoId}${t}`
}

// Two-dot step indicator. current is 0-based.
function StepDots({ current }) {
  return (
    <div style={{ display:'flex', justifyContent:'center', gap:8, marginBottom:30 }}>
      {[0, 1].map(i => (
        <div key={i} style={{
          width: i === current ? 26 : 7, height:7, borderRadius:4,
          background: i === current ? ACCENT : 'rgba(31,27,22,0.15)',
          transition:'width 220ms, background 220ms',
        }}/>
      ))}
    </div>
  )
}

export default function ChurchOnboarding({ resumeChurch = null, onExplore, onClose, onDone, addMode = false }) {
  const { updateProfile, refreshProfile, refreshChurches } = useAuth()
  const { saveSermon } = useSermonAdmin()

  // 'basics' → 'wow'. Resume (church already exists) jumps straight to the scan.
  const [step, setStep] = useState(resumeChurch ? 'wow' : 'basics')

  // Step 1 — church basics
  const [churchName, setChurchName] = useState(resumeChurch?.name || '')
  const [location,   setLocation]   = useState(resumeChurch?.location || '')

  // Step 1 — optional YouTube channel → logo preview (FIX-CHURCH-LOGOS.md).
  // The church doesn't exist yet at this point (ensureChurch() only runs once
  // the user finishes the scan step), so this just previews the icon via
  // fetch-church-logo's no-church_id mode; the choice is applied once the
  // church row actually exists, inside ensureChurch().
  const [youtubeChannelUrl, setYoutubeChannelUrl] = useState('')
  const [logoPreview, setLogoPreview] = useState(null) // { dataUrl, channelTitle } | null
  const [logoChoice, setLogoChoice] = useState(null)   // 'youtube' | 'upload' | 'initials' | null
  const [logoPreviewLoading, setLogoPreviewLoading] = useState(false)
  const [logoPreviewError, setLogoPreviewError] = useState('')
  const [uploadedLogoDataUrl, setUploadedLogoDataUrl] = useState(null)
  const uploadedLogoFileRef = useRef(null)
  const logoFetchedRef = useRef('')

  useEffect(() => {
    const url = youtubeChannelUrl.trim()
    if (!url || url.length < 4) { setLogoPreview(null); setLogoPreviewError(''); return }
    if (logoFetchedRef.current === url) return
    const t = setTimeout(async () => {
      logoFetchedRef.current = url
      setLogoPreviewLoading(true); setLogoPreviewError('')
      try {
        const { data, error } = await supabase.functions.invoke('fetch-church-logo', { body: { channel_url: url } })
        if (error || data?.error) throw new Error(data?.error || error?.message || 'Could not find that channel.')
        setLogoPreview({ dataUrl: data.image_data_url, channelTitle: data.channel_title || '' })
        setLogoChoice('youtube')
      } catch (err) {
        setLogoPreview(null)
        setLogoPreviewError(err.message || 'Could not find that channel.')
      } finally {
        setLogoPreviewLoading(false)
      }
    }, 700)
    return () => clearTimeout(t)
  }, [youtubeChannelUrl])

  function handleLogoFileChange(e) {
    const file = e.target.files?.[0]
    if (!file) return
    uploadedLogoFileRef.current = file
    const reader = new FileReader()
    reader.onload = () => { setUploadedLogoDataUrl(reader.result); setLogoChoice('upload') }
    reader.readAsDataURL(file)
  }

  // Best-effort — applied once the church row exists (see ensureChurch()).
  // Never blocks church creation if it fails; the church just keeps its
  // default 'initials' logo_source and can be fixed later from Settings.
  async function applyLogoChoice(churchId) {
    if (!churchId) return
    try {
      if (logoChoice === 'youtube' && youtubeChannelUrl.trim()) {
        await supabase.functions.invoke('fetch-church-logo', {
          body: { channel_url: youtubeChannelUrl.trim(), church_id: churchId },
        })
      } else if (logoChoice === 'upload' && uploadedLogoFileRef.current) {
        const file = uploadedLogoFileRef.current
        const ext = (file.name.split('.').pop() || 'png').toLowerCase()
        const path = `${churchId}.${ext}`
        await supabase.storage.from('church-logos').upload(path, file, { upsert: true, contentType: file.type || 'image/png' })
        const { data: pub } = supabase.storage.from('church-logos').getPublicUrl(path)
        await supabase.from('churches').update({
          logo_url: `${pub.publicUrl}?v=${Date.now()}`, logo_source: 'upload', logo_updated_at: new Date().toISOString(),
        }).eq('id', churchId)
      }
    } catch (e) {
      console.warn('[ChurchOnboarding] logo apply failed:', e)
    }
  }

  // Step 2 — scan. Up to MAX_URLS YouTube URLs.
  const [urls, setUrls] = useState([''])
  const [metaCache, setMetaCache] = useState({}) // videoId -> { title, author, thumbnail }
  const fetchedRef = useRef(new Set())           // videoIds we've already oEmbed-fetched
  // 'input' | 'scanning' | 'success' | 'error'
  const [phase,     setPhase]     = useState('input')
  const [scanMsgIdx, setScanMsgIdx] = useState(0)
  const [scanProgress, setScanProgress] = useState({ done: 0, total: 0 })
  const [scanElapsedMs, setScanElapsedMs] = useState(0)
  const [result,    setResult]    = useState(null) // { count, refs, firstRef, videos }
  const [scanError, setScanError] = useState('')
  const [busy,      setBusy]      = useState(false)

  const today = new Date().toISOString().slice(0, 10)

  // De-duplicated list of valid { idx, videoId } entries across all URL slots.
  const validEntries = (() => {
    const out = []
    const seen = new Set()
    urls.forEach((u, idx) => {
      const videoId = extractVideoId(u)
      if (videoId && !seen.has(videoId)) { seen.add(videoId); out.push({ idx, videoId }) }
    })
    return out
  })()
  const idKey = validEntries.map(e => e.videoId).join(',')

  // ── oEmbed preview for each valid URL (fetched once per video id) ──
  useEffect(() => {
    let cancelled = false
    validEntries.forEach(({ videoId: id }) => {
      if (fetchedRef.current.has(id)) return
      fetchedRef.current.add(id)
      const watch = `https://www.youtube.com/watch?v=${id}`
      fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(watch)}&format=json`)
        .then(r => (r.ok ? r.json() : Promise.reject(new Error('oEmbed'))))
        .then(meta => {
          if (cancelled) return
          setMetaCache(c => ({ ...c, [id]: {
            title: meta.title || 'YouTube sermon',
            author: meta.author_name || '',
            thumbnail: meta.thumbnail_url || `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
          } }))
        })
        .catch(() => {
          if (cancelled) return
          setMetaCache(c => ({ ...c, [id]: { title: 'YouTube sermon', author: '', thumbnail: `https://i.ytimg.com/vi/${id}/hqdefault.jpg` } }))
        })
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idKey])

  function setUrlAt(i, v) { setUrls(prev => prev.map((u, idx) => (idx === i ? v : u))) }
  function addUrl()      { setUrls(prev => (prev.length >= MAX_URLS ? prev : [...prev, ''])) }
  function removeUrl(i)  { setUrls(prev => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i))) }

  // ── Cycle the animated status messages while scanning ──
  useEffect(() => {
    if (phase !== 'scanning') { setScanMsgIdx(0); return }
    const id = setInterval(() => {
      setScanMsgIdx(i => Math.min(i + 1, SCAN_STEPS.length - 1))
    }, 1500)
    return () => clearInterval(id)
  }, [phase])

  // ── Tick an elapsed-time counter while scanning (drives the progress bar) ──
  useEffect(() => {
    if (phase !== 'scanning') { setScanElapsedMs(0); return }
    const startedAt = Date.now()
    setScanElapsedMs(0)
    const id = setInterval(() => setScanElapsedMs(Date.now() - startedAt), 200)
    return () => clearInterval(id)
  }, [phase])

  // ── Fetch transcript from the YouTube URL via the claude-proxy edge fn ──
  // Use supabase.functions.invoke (not a raw fetch) so the caller's session
  // JWT is attached — claude-proxy runs with verify_jwt on, so a raw fetch
  // with a non-JWT anon key gets rejected as "invalid JWT".
  const fetchTranscript = useCallback(async (youtubeUrl) => {
    const { data, error } = await supabase.functions.invoke('claude-proxy', {
      body: { action: 'fetch_transcript', youtube_url: youtubeUrl },
    })
    if (error) {
      const upstream = data?.error?.message || data?.message || ''
      throw new Error(upstream || error.message || 'Could not fetch the transcript.')
    }
    if (data?.error) throw new Error(data.error.message || JSON.stringify(data.error))
    const plain = data?.transcript_only_text || ''
    const segs  = Array.isArray(data?.transcript) ? data.transcript : null
    const text  = plain || flattenSegs(segs)
    if (!text) throw new Error('No transcript available for that video.')
    // Absolute upload date if the transcript response carried one (often it
    // doesn't — see fetchVideoInfoDate for the fallback).
    const apiDate = extractApiDateFromMeta(data).date || null
    return { text, segs, apiDate }
  }, [])

  // Dedicated video-info call to recover the exact upload date when the
  // transcript response didn't include one (mirrors the bulk importer).
  const fetchVideoInfoDate = useCallback(async (youtubeUrl) => {
    try {
      const { data, error } = await supabase.functions.invoke('claude-proxy', {
        body: { action: 'fetch_video_info', youtube_url: youtubeUrl },
      })
      if (error || !data || data.error) return null
      return extractApiDateFromMeta(data).date || null
    } catch { return null }
  }, [])

  // Create (or reuse) the church and make this user its admin. Returns the
  // church id, or throws on failure.
  //
  // Prefer the `onboard_church` SECURITY DEFINER RPC: it inserts the church and
  // promotes the caller to admin atomically, server-side, so it works without a
  // per-table INSERT policy on churches (the cause of the "row-level security"
  // error). Falls back to a direct insert + profile update for deployments that
  // added the onboarding RLS policies instead of the function.
  async function ensureChurch() {
    if (resumeChurch?.id) return resumeChurch.id

    const rpc = await supabase.rpc('onboard_church', {
      p_name: churchName.trim(),
      p_location: location.trim() || null,
    })
    if (!rpc.error && rpc.data) {
      try { await refreshProfile?.() } catch {}
      await applyLogoChoice(rpc.data)
      return rpc.data
    }

    // Fallback path (requires the onboarding INSERT/UPDATE RLS policies).
    const payload = { name: churchName.trim(), slug: nameToSlug(churchName) }
    if (location.trim()) payload.location = location.trim()
    const { data: newChurch, error: chErr } = await supabase
      .from('churches').insert(payload).select().single()
    if (chErr) {
      const detail = rpc.error?.message || chErr.message
      throw new Error(`Could not create your church: ${detail}`)
    }
    // Become admin BEFORE saving sermons so RLS (get_my_role / get_my_church)
    // permits the inserts. Also mark onboarding complete — finishing church
    // set-up means the user is fully onboarded (this can run mid-onboarding).
    const { error: profErr } = await updateProfile({ church_id: newChurch.id, role: 'admin', onboarding_complete: true })
    if (profErr) throw new Error(`Could not set up your account: ${profErr.message}`)
    await applyLogoChoice(newChurch.id)
    return newChurch.id
  }

  // ── The full scan + persist flow (1–5 videos) ──
  async function runScan() {
    const entries = validEntries
    if (entries.length === 0) return
    setScanError(''); setPhase('scanning'); setScanMsgIdx(0)
    setScanProgress({ done: 0, total: entries.length })

    try {
      // Resume case: the church already exists, so enforce the paywall before
      // spending scans. A brand-new church always has its full free allowance.
      if (resumeChurch?.id) {
        const gate = await canScan(resumeChurch.id, entries.length)
        if (!gate.allowed) {
          setScanError(gate.message || "You've used all your free sermon scans.")
          setPhase('error')
          return
        }
      }

      // 1 + 2. Fetch transcript + scan refs for each video. Skip ones that
      // fail (no captions etc.) and keep going.
      const scanned = [] // { videoId, meta, text, refs, date }
      for (const e of entries) {
        const meta = metaCache[e.videoId] || { title: 'Sermon', author: '', thumbnail: '' }
        try {
          const youtubeUrl = `https://www.youtube.com/watch?v=${e.videoId}`
          const { text, segs, apiDate } = await fetchTranscript(youtubeUrl)
          // Real upload date: transcript API date → video-info API date →
          // date parsed from the title → today.
          let sermonDate = apiDate
          if (!sermonDate) sermonDate = await fetchVideoInfoDate(youtubeUrl)
          if (!sermonDate) sermonDate = resolveVideoDate({ title: meta.title })
          if (!sermonDate) sermonDate = today
          const refs = await scanTranscript(text, { title: meta.title || 'Sermon', series: '', date: sermonDate }, segs)
          const cleanRefs = Array.isArray(refs) ? refs.filter(r => r && (r.verseRef || r.verse_ref)) : []
          const aiSummary = await generateSermonSummary(text, { title: meta.title || 'Sermon', preacher: meta.author || '', date: sermonDate, church_name: churchName || '' })
          scanned.push({ videoId: e.videoId, meta, text, refs: cleanRefs, date: sermonDate, aiSummary })
        } catch (err) {
          console.warn('[ChurchOnboarding] scan failed for', e.videoId, err)
        }
        setScanProgress(p => ({ done: p.done + 1, total: p.total }))
      }

      if (scanned.length === 0) {
        setScanError("We couldn't scan any of those videos. They may not have subtitles enabled.")
        setPhase('error')
        return
      }

      // 3 + 4. Resolve the church (create + become admin, or reuse on resume).
      const churchId = await ensureChurch()

      // 5. Save each sermon + its references against the church.
      for (const sc of scanned) {
        const youtubeUrl = `https://www.youtube.com/watch?v=${sc.videoId}`
        const { error: saveErr } = await saveSermon(
          {
            title: sc.meta.title || 'Sermon',
            series: '',
            preacher: sc.meta.author || '',
            date: sc.date || today,
            youtubeId: sc.videoId,
            youtubeUrl,
            duration: '',
            transcript: sc.text,
            churchId,
            aiSummary: sc.aiSummary,
          },
          sc.refs,
        )
        if (saveErr) console.warn('[ChurchOnboarding] save failed:', saveErr)
      }

      // 6. Count these scans + mark setup complete on the church.
      await recordScans(churchId, scanned.length)
      const { error: flagErr } = await supabase
        .from('churches').update({ setup_complete: true }).eq('id', churchId)
      if (flagErr) console.warn('[ChurchOnboarding] setup_complete update failed:', flagErr)

      // Refresh context so the rest of the app sees the new church/role.
      try { await refreshChurches?.() } catch {}
      try { await refreshProfile?.() } catch {}

      // Tag each ref with its source video so the reveal can deep-link to the
      // timestamped moment on YouTube.
      const allRefs = scanned.flatMap(sc => sc.refs.map(r => ({ ...r, _videoId: sc.videoId })))
      setResult({
        count: allRefs.length,
        refs: allRefs,
        firstRef: allRefs[0] || null,
        videos: scanned.length,
        churchId,
      })
      setPhase('success')
    } catch (e) {
      console.error('[ChurchOnboarding] scan failed:', e)
      setScanError(e?.message || 'Something went wrong while scanning.')
      setPhase('error')
    }
  }

  // "Skip for now" — create the church (so their basics aren't lost), make the
  // user admin, mark setup complete, and drop them into the app without
  // scanning. On resume there's nothing to create — just dismiss.
  async function skipScan() {
    if (busy) return
    setBusy(true)
    try {
      const churchId = await ensureChurch()
      await supabase.from('churches').update({ setup_complete: true }).eq('id', churchId)
      try { await refreshChurches?.() } catch {}
      try { await refreshProfile?.() } catch {}
    } catch (e) {
      console.warn('[ChurchOnboarding] skip failed:', e)
    } finally {
      setBusy(false)
    }
    if (onExplore) onExplore(null)
    else if (onClose) onClose()
  }

  function tryAgain() { setPhase('input'); setScanError('') }

  const basicsValid = churchName.trim().length > 1 && location.trim().length > 1
  const scanCount = validEntries.length

  // ─────────────────────────────────────────────────────────────────
  // STEP 1 — Church basics
  // ─────────────────────────────────────────────────────────────────
  if (step === 'basics') {
    return (
      <div style={s.shell}>
        <div style={s.body}>
          {onClose && (
            <button type="button" onClick={() => onClose()}
              style={{ background:'none', border:'none', color:'var(--stone)', fontSize:14, fontWeight:600, cursor:'pointer', padding:0, marginBottom:18, fontFamily:SANS }}>
              ← Back
            </button>
          )}
          <div style={s.kicker}><span style={s.kline}/><span style={s.klabel}>The Bible Community</span></div>
          <StepDots current={0}/>
          <h1 style={s.title}>Set up your church</h1>
          <p style={s.subtitle}>A couple of details and we'll bring your sermons to life.</p>

          <label style={s.label}>Church name</label>
          <input
            style={{ ...s.inputLg, marginBottom:18 }}
            value={churchName}
            onChange={e => setChurchName(e.target.value)}
            placeholder="e.g. New Life Church"
            autoFocus
          />

          <label style={s.label}>Location</label>
          <input
            style={{ ...s.input, marginBottom:18 }}
            value={location}
            onChange={e => setLocation(e.target.value)}
            placeholder="e.g. Tunbridge Wells, Kent"
          />

          <label style={s.label}>YouTube channel (optional)</label>
          <input
            style={{ ...s.input, marginBottom:8 }}
            value={youtubeChannelUrl}
            onChange={e => { setYoutubeChannelUrl(e.target.value); setLogoChoice(null); setLogoPreview(null) }}
            placeholder="youtube.com/@yourchurch"
          />
          {logoPreviewLoading && <div style={{ fontSize:12.5, color:'var(--stone)', marginBottom:14 }}>Looking up your channel…</div>}
          {logoPreviewError && <div style={{ fontSize:12.5, color:'#C8372A', marginBottom:14 }}>{logoPreviewError}</div>}
          {logoPreview && (
            <div style={{ display:'flex', alignItems:'center', gap:12, background:'var(--white)', border:'1px solid var(--border-strong)', borderRadius:14, padding:12, marginBottom:8 }}>
              <img src={logoChoice === 'upload' && uploadedLogoDataUrl ? uploadedLogoDataUrl : logoPreview.dataUrl} alt=""
                style={{ width:48, height:48, borderRadius:12, objectFit:'cover', flexShrink:0 }}/>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:700, color:'var(--ink)', marginBottom:6 }}>Is this your church's logo?</div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <button type="button" onClick={() => setLogoChoice('youtube')}
                    style={{ fontSize:12, fontWeight:700, padding:'6px 10px', borderRadius:999, border:'none', cursor:'pointer', fontFamily:SANS, background: logoChoice==='youtube' ? ACCENT : 'var(--border)', color: logoChoice==='youtube' ? NAVY : 'var(--stone)' }}>
                    Use this
                  </button>
                  <label style={{ fontSize:12, fontWeight:700, padding:'6px 10px', borderRadius:999, cursor:'pointer', fontFamily:SANS, background: logoChoice==='upload' ? ACCENT : 'var(--border)', color: logoChoice==='upload' ? NAVY : 'var(--stone)' }}>
                    Upload a different one
                    <input type="file" accept="image/*" style={{ display:'none' }} onChange={handleLogoFileChange}/>
                  </label>
                  <button type="button" onClick={() => setLogoChoice('initials')}
                    style={{ fontSize:12, fontWeight:700, padding:'6px 10px', borderRadius:999, border:'none', cursor:'pointer', fontFamily:SANS, background: logoChoice==='initials' ? ACCENT : 'var(--border)', color: logoChoice==='initials' ? NAVY : 'var(--stone)' }}>
                    Use initials
                  </button>
                </div>
              </div>
            </div>
          )}

          <button
            type="button"
            disabled={!basicsValid}
            onClick={() => setStep('wow')}
            style={{ ...s.primary, marginTop:24, ...(basicsValid ? {} : s.dis) }}
          >
            Next →
          </button>
        </div>
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────────
  // STEP 2 — The wow moment
  // ─────────────────────────────────────────────────────────────────

  // SCANNING
  if (phase === 'scanning') {
    const multi = scanProgress.total > 1
    // Fake-progress that eases towards 95% over the rough estimate, then crawls
    // — never hits 100% until the real work resolves and we change phase.
    const estMs = Math.max(1, scanProgress.total || 1) * 14000
    const ratio = scanElapsedMs / estMs
    const pct = ratio < 1
      ? Math.min(92, ratio * 92)
      : Math.min(98, 92 + (1 - Math.exp(-(ratio - 1) * 0.6)) * 6)
    const elapsedSec = Math.round(scanElapsedMs / 1000)
    return (
      <div style={s.shell}>
        <style>{`
          @keyframes co-pulse { 0%,100% { transform: scale(1); opacity:1; } 50% { transform: scale(1.16); opacity:0.55; } }
          @keyframes co-ring  { to { transform: rotate(360deg); } }
          @keyframes co-fade  { from { opacity:0; transform: translateY(6px); } to { opacity:1; transform:none; } }
          @keyframes co-shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
        `}</style>
        <div style={{ ...s.body, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center', paddingTop:80 }}>
          <div style={{ position:'relative', width:88, height:88, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:30 }}>
            <span style={{ position:'absolute', inset:0, border:`3px solid rgba(232,200,122,0.16)`, borderTopColor:ACCENT, borderRadius:'50%', animation:'co-ring 1.1s linear infinite' }}/>
            <span style={{ fontSize:38, animation:'co-pulse 1.5s ease-in-out infinite' }}>📖</span>
          </div>
          {multi && (
            <div style={{ fontSize:12, fontWeight:800, letterSpacing:'0.14em', textTransform:'uppercase', color:'var(--gold-ink)', marginBottom:10 }}>
              Sermon {Math.min(scanProgress.done + 1, scanProgress.total)} of {scanProgress.total}
            </div>
          )}
          <div key={scanMsgIdx} style={{ fontFamily:SERIF, fontSize:24, color:'var(--ink)', animation:'co-fade 360ms ease', marginBottom:18 }}>
            {SCAN_STEPS[scanMsgIdx]}
          </div>

          {/* Progress bar + elapsed-time readout so it's not just a spinner. */}
          <div style={{ width:'100%', maxWidth:320, background:'var(--border)', borderRadius:999, overflow:'hidden', height:8, position:'relative' }}>
            <div style={{ width:`${pct}%`, height:'100%', background:`linear-gradient(90deg, ${ACCENT}, #C9A85A)`, borderRadius:999, transition:'width 240ms linear', position:'relative', overflow:'hidden' }}>
              <div style={{ position:'absolute', inset:0, background:'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)', animation:'co-shimmer 1.6s ease-in-out infinite' }}/>
            </div>
          </div>
          <div style={{ marginTop:10, fontSize:12.5, color:'var(--stone)' }}>
            {elapsedSec}s elapsed{multi ? ` · usually ~${Math.round(estMs / 1000)}s` : ' · usually ~10–15s'}
          </div>

          <div style={{ fontSize:13.5, color:'var(--stone)', maxWidth:300, lineHeight:1.5, marginTop:14 }}>
            Please keep this screen open while we scan - if you leave the app it won't finish. It usually only takes a few seconds.
          </div>
          <div style={{ fontSize:13.5, color:'var(--gold-ink)', maxWidth:300, lineHeight:1.5, marginTop:18, fontWeight:600 }}>
            Once you've uploaded your first sermon we can help you upload the rest automatically.
          </div>
        </div>
      </div>
    )
  }

  // SUCCESS
  if (phase === 'success' && result) {
    return (
      <div style={s.shell}>
        <style>{`@keyframes co-rise { from { opacity:0; transform: translateY(14px); } to { opacity:1; transform:none; } }`}</style>
        <div style={{ ...s.body, animation:'co-rise 480ms ease' }}>
          <div style={s.kicker}><span style={s.kline}/><span style={s.klabel}>{result.videos > 1 ? `${result.videos} sermons scanned` : 'Sermon scanned'}</span></div>

          <div style={{ fontFamily:SERIF, fontSize:46, lineHeight:1.05, color:'var(--gold-ink)', marginBottom:4 }}>
            {result.count}
          </div>
          <div style={{ fontFamily:SERIF, fontSize:26, color:'var(--ink)', marginBottom:4 }}>
            Bible reference{result.count === 1 ? '' : 's'} found
          </div>
          <div style={{ fontSize:13.5, color:'var(--stone)', marginBottom:22, lineHeight:1.5 }}>
            {result.videos > 1 ? `across ${result.videos} sermons — ` : ''}now marked in your congregation's Bible.
          </div>

          {/* Full reference list with the AI summary for each — the same detail
              an admin sees when reviewing a scan. */}
          {result.refs.length > 0 && (
            <div style={{ marginBottom:22 }}>
              {result.refs.map((r, i) => {
                const ts = fmtTimestamp(r)
                const watchUrl = watchAtUrl(r._videoId, r)
                return (
                  <div key={i} style={{ background:'var(--white)', border:'1px solid var(--border)', borderRadius:12, padding:'13px 15px', marginBottom:8 }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, marginBottom: r.notes ? 8 : 0 }}>
                      <div style={{ fontSize:14, fontWeight:800, color:'var(--gold-ink)', letterSpacing:'0.02em' }}>
                        {r.verseRef || r.verse_ref}
                      </div>
                      {watchUrl && (
                        <a href={watchUrl} target="_blank" rel="noreferrer"
                          style={{ display:'inline-flex', alignItems:'center', gap:6, flexShrink:0, fontSize:11.5, fontWeight:600, color:'var(--ink)', textDecoration:'none', letterSpacing:'0.02em', background:'rgba(31,27,22,0.05)', border:'1px solid var(--border-strong)', borderRadius:999, padding:'5px 11px' }}>
                          <YouTubeIcon size={13}/> WATCH{ts ? ` ${ts}` : ''}
                        </a>
                      )}
                    </div>
                    {r.notes && (
                      <div style={{ fontSize:13.5, color:'var(--ink)', lineHeight:1.55 }}>{r.notes}</div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {addMode ? (
            <button type="button" style={s.primary} onClick={() => onDone?.()}>
              Done
            </button>
          ) : (
            <button type="button" style={s.primary} onClick={() => onExplore?.(result.firstRef)}>
              Explore your Bible →
            </button>
          )}
        </div>
      </div>
    )
  }

  // ERROR
  if (phase === 'error') {
    return (
      <div style={s.shell}>
        <div style={{ ...s.body, paddingTop:64 }}>
          <div style={{ fontSize:40, marginBottom:18 }}>😕</div>
          <h1 style={{ ...s.title, fontSize:28 }}>We couldn't scan that</h1>
          <p style={s.subtitle}>The video may not have subtitles enabled. Try a different sermon video with captions available.</p>
          {scanError && <div style={s.err}>{scanError}</div>}
          <button type="button" style={{ ...s.primary, marginTop:22 }} onClick={tryAgain}>
            Try again
          </button>
          {(onExplore || onClose) && (
            <button type="button" style={s.ghost} onClick={skipScan} disabled={busy}>
              {busy ? 'Skipping…' : 'Skip for now'}
            </button>
          )}
        </div>
      </div>
    )
  }

  // INPUT (default Step 2 view) — up to MAX_URLS URLs
  return (
    <div style={s.shell}>
      <style>{`@keyframes co-rise { from { opacity:0; transform: translateY(10px); } to { opacity:1; transform:none; } }`}</style>
      <div style={s.body}>
        {(!resumeChurch || onClose) && (
          <button type="button"
            onClick={() => { if (!resumeChurch) setStep('basics'); else onClose?.(); }}
            style={{ background:'none', border:'none', color:'var(--stone)', fontSize:14, fontWeight:600, cursor:'pointer', padding:0, marginBottom:18, fontFamily:SANS }}>
            ← Back
          </button>
        )}
        <div style={s.kicker}><span style={s.kline}/><span style={s.klabel}>The Bible Community</span></div>
        <StepDots current={1}/>
        <h1 style={s.title}>Let's bring your sermons to life</h1>
        <p style={s.subtitle}>Paste one of your sermon YouTube URLs and we'll show you all the bible references and sermon notes that can be added to your congregation's Bible.</p>

        <label style={s.label}>Sermon video URL{urls.length > 1 ? 's' : ''}</label>

        {urls.map((u, i) => {
          const vid = extractVideoId(u)
          const meta = vid ? metaCache[vid] : null
          const showErr = u.trim() && !vid
          return (
            <div key={i} style={{ marginBottom: 12 }}>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <input
                  style={{ ...s.input, flex:1 }}
                  value={u}
                  onChange={e => setUrlAt(i, e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                  autoFocus={i === 0}
                  inputMode="url"
                  autoCapitalize="off"
                  autoCorrect="off"
                />
                {urls.length > 1 && (
                  <button type="button" onClick={() => removeUrl(i)}
                    aria-label="Remove"
                    style={{ flexShrink:0, width:40, height:40, borderRadius:10, background:'transparent', border:'1px solid var(--border-strong)', color:'var(--stone)', fontSize:15, cursor:'pointer', lineHeight:1 }}>
                    ✕
                  </button>
                )}
              </div>
              {showErr && <div style={{ ...s.err, marginTop:8 }}>Please paste a YouTube video URL</div>}
              {vid && meta && (
                <div style={{ display:'flex', gap:10, alignItems:'center', marginTop:8, background:'var(--white)', border:'1px solid var(--border)', borderRadius:10, padding:8, animation:'co-rise 300ms ease' }}>
                  <img src={meta.thumbnail} alt="" style={{ width:64, height:36, objectFit:'cover', borderRadius:6, flexShrink:0, background:'#000' }} onError={e => { e.target.style.visibility = 'hidden' }}/>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:'var(--ink)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{meta.title}</div>
                    {meta.author && <div style={{ fontSize:11.5, color:'var(--stone)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{meta.author}</div>}
                  </div>
                </div>
              )}
            </div>
          )
        })}

        {urls.length < MAX_URLS && (
          <button type="button" onClick={addUrl}
            style={{ width:'100%', background:'transparent', border:'1px dashed var(--border-strong)', borderRadius:12, padding:'12px', color:'var(--stone)', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:SANS, marginTop:2, marginBottom:6 }}>
            + Add another sermon
          </button>
        )}

        <button
          type="button"
          disabled={scanCount === 0}
          onClick={runScan}
          style={{ ...s.primary, marginTop:10, ...(scanCount === 0 ? s.dis : {}) }}
        >
          {scanCount > 1 ? `Scan ${scanCount} sermons →` : 'Scan this sermon →'}
        </button>

        <button type="button" onClick={skipScan} disabled={busy} style={s.ghost}>
          {busy ? 'Skipping…' : 'Skip for now'}
        </button>
      </div>
    </div>
  )
}
